import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { regionService, countryService } from '@/lib/api/services/region'
import type { ApiResponse, PaginatedResponse, Region, RegionDetail, MemberCountryRegion, Country, CountryFilterParams, RegionFilterParams, UpdateRegionStatus } from '@/lib/api/types'

// Query keys
export const regionKeys = {
  all: ['regions'] as const,
  lists: () => [...regionKeys.all, 'list'] as const,
  list: (filters?: RegionFilterParams) => [...regionKeys.lists(), filters] as const,
  details: () => [...regionKeys.all, 'detail'] as const,
  detail: (id: string) => [...regionKeys.details(), id] as const,
  countries: (id: string) => [...regionKeys.detail(id), 'countries'] as const,
  stats: () => [...regionKeys.all, 'stats'] as const,
  memberCountryRegions: () => [...regionKeys.all, 'memberCountryRegions'] as const
}

export const countryKeys = {
  all: ['countries'] as const,
  lists: () => [...countryKeys.all, 'list'] as const,
  list: (filters?: CountryFilterParams) => [...countryKeys.lists(), filters] as const,
  details: () => [...countryKeys.all, 'detail'] as const,
  detail: (id: string) => [...countryKeys.details(), id] as const,
  search: (query: string) => [...countryKeys.all, 'search', query] as const
}

// Hook to fetch all regions with filters
export function useAllRegions(filters?: RegionFilterParams) {
  return useQuery<ApiResponse<PaginatedResponse<Region>>, Error>({
    queryKey: regionKeys.list(filters),
    queryFn: () => regionService.getAllRegions(filters)
  })
}

// Hook to fetch member country regions (for member countries page)
export function useMemberCountryRegions() {
  return useQuery<ApiResponse<MemberCountryRegion[]>, Error>({
    queryKey: regionKeys.memberCountryRegions(),
    queryFn: () => regionService.getMemberCountryRegions()
  })
}

// Hook to fetch a single region
export function useRegion(regionId?: string) {
  return useQuery<ApiResponse<RegionDetail>, Error>({
    queryKey: regionKeys.detail(regionId!),
    queryFn: () => regionService.getRegionById(regionId!),
    enabled: !!regionId
  })
}

// Hook to fetch countries by region (deprecated - use useCountries instead)
export function useCountriesByRegion(regionId?: string) {
  return useQuery<ApiResponse<Country[]>, Error>({
    queryKey: regionKeys.countries(regionId!),
    queryFn: () => regionService.getCountriesByRegion(regionId!),
    enabled: !!regionId
  })
}

// Hook to fetch countries with filters (regionTag, page, size)
export function useCountries(filters?: CountryFilterParams) {
  return useQuery<ApiResponse<PaginatedResponse<Country>>, Error>({
    queryKey: countryKeys.list(filters),
    queryFn: () => countryService.getCountries(filters),
    enabled: !!filters?.regionTag // Only fetch if regionTag is provided
  })
}

// Hook to fetch all countries (without filters)
export function useAllCountries() {
  return useQuery<ApiResponse<Country[]>, Error>({
    queryKey: countryKeys.lists(),
    queryFn: () => countryService.getAllCountries()
  })
}

// Hook to fetch a single country
export function useCountry(countryId?: string) {
  return useQuery<ApiResponse<Country>, Error>({
    queryKey: countryKeys.detail(countryId!),
    queryFn: () => countryService.getCountryById(countryId!),
    enabled: !!countryId
  })
}

// Hook to update region status
export function useUpdateRegionStatus() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    { regionId: string; data: UpdateRegionStatus }
  >({
    mutationFn: ({ regionId, data }) =>
      regionService.updateRegionStatus(regionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: regionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: regionKeys.stats() })
    }
  })
}

// Hook to fetch published regions count
export function usePublishedRegionsCount() {
  return useQuery<ApiResponse<number>, Error>({
    queryKey: [...regionKeys.stats(), 'published'],
    queryFn: () => regionService.getPublishedRegionsCount()
  })
}

// Hook to fetch draft regions count
export function useDraftRegionsCount() {
  return useQuery<ApiResponse<number>, Error>({
    queryKey: [...regionKeys.stats(), 'draft'],
    queryFn: () => regionService.getDraftRegionsCount()
  })
}

// Hook to search countries
export function useSearchCountries(
  searchBy?: string,
  enabled: boolean = true
) {
  return useQuery<ApiResponse<Country[]>, Error>({
    queryKey: countryKeys.search(searchBy || ''),
    queryFn: () => countryService.searchCountries(searchBy),
    enabled: enabled && !!searchBy && searchBy.length > 0,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}
